// Monstres posés sur les cartes (légendaires…) : le joueur leur parle pour les affronter. Chaque joueur a sa
// propre rencontre : le monstre reste tant qu'il n'a pas été capturé ou mis K.O. par ce joueur (et, s'il revient,
// il réapparaît quelques minutes après avoir été mis K.O. sans être capturé). Un monstre non affrontable n'est qu'un
// décor vivant : il peut se promener comme un PNJ.
import type { MonsterSprite } from './monster-sprites.ts';
import { isKeyOf } from './guards.ts';
import type { NpcMovement } from './npc-movement.ts';
import type { Direction } from './protocol.ts';
import { reachedStep, type QuestProgress } from './quests.ts';

/** Un monstre posé sur une case de la carte. */
export interface MapMonster {
  /** Identifiant stable (le suivi « déjà capturé ou vaincu » des joueurs s'y rattache). */
  id: string;
  x: number;
  y: number;
  speciesId: number;
  level: number;
  shiny: boolean;
  /** Répliques avant le combat (pour un décor : quand on lui parle), une par ligne (vide : phrase par défaut, ou rien pour un décor). */
  dialogue: string;
  /** Le joueur peut l'affronter ; sinon c'est un décor (il peut alors se déplacer). */
  battle: boolean;
  /** Mis K.O. sans être capturé, il revient pour ce joueur au bout de ce nombre de minutes (null : jamais). */
  returnMinutes: number | null;
  /** Déplacements, comme un PNJ (seulement s'il n'est pas affrontable). */
  movement: NpcMovement;
  /** Il n'apparaît que pour les joueurs qui remplissent cette condition de quête (null : pour tout le monde). */
  requires: QuestCondition | null;
}

/**
 * Où en est le joueur dans une quête : `active` en cours, `completed` terminée, `started` l'une ou l'autre. Avec `step`
 * (à partir de 0 ; sans effet sur `completed`) : seulement à partir de cette étape de la quête.
 */
export interface QuestCondition {
  questId: number;
  status: 'active' | 'completed' | 'started';
  step: number | null;
}

export const QUEST_CONDITION_STATUSES: Readonly<Record<QuestCondition['status'], string>> = {
  active: 'est en cours', completed: 'est terminée', started: 'a été commencée (en cours ou terminée)',
};

export const isQuestConditionStatus = isKeyOf<QuestCondition['status']>(QUEST_CONDITION_STATUSES);

/** La condition est-elle remplie, d'après où en est le joueur dans ses quêtes ? */
export function isQuestConditionMet({ questId, status, step }: QuestCondition, progress: QuestProgress): boolean {
  const entry = progress.get(questId);
  if (status === 'completed') return entry?.status === 'completed';
  if (status === 'active' && entry?.status !== 'active') return false;
  if (step !== null) return status === 'active' ? entry !== undefined && entry.step >= step : reachedStep(questId, step, progress);
  return entry !== undefined;
}

/** Comment un joueur en a fini avec un monstre posé. */
export interface MapMonsterOutcome {
  captured: boolean;
  /** Date (ms) de la capture ou du K.O. */
  at: number;
}

/**
 * Le monstre a-t-il disparu pour ce joueur ? Oui s'il l'a capturé, ou mis K.O. — sauf s'il revient et que
 * `returnMinutes` minutes ont passé depuis. Un décor (non affrontable) ne disparaît jamais.
 */
export function isMapMonsterGone(monster: Pick<MapMonster, 'battle' | 'returnMinutes'>, outcome: MapMonsterOutcome | undefined, now: number): boolean {
  if (!outcome || !monster.battle) return false;
  if (outcome.captured || monster.returnMinutes === null) return true;
  return now - outcome.at < monster.returnMinutes * 60_000;
}

/** Ce que le joueur voit d'un monstre posé. */
export interface MapMonsterView {
  id: string;
  x: number;
  y: number;
  name: string;
  image: string;
  sprite: MonsterSprite | null;
  shiny: boolean;
  /** Taille affichée, celle de son espèce (voir MonsterSpecies.mapSize). */
  size: number;
  dir: Direction;
  /** Ce que fait la touche d'action en face : l'affronter, lire ses répliques (décor qui en a), ou rien. */
  action: 'battle' | 'talk' | null;
}

/** Pixels d'une case, pour la taille des monstres sur la carte : celle des planches de décor. */
export const MAP_MONSTER_CELL_PX = 16;
/** Taille par défaut d'une espèce sur la carte (monstre posé, acteur de cinématique) : une case et demie. */
export const MAP_MONSTER_DEFAULT_SIZE = 24;
export const MAP_MONSTER_SIZE_LIMITS = { min: 4, max: 256 } as const;
export const MAP_MONSTER_LIMITS = { maxPerMap: 100, maxLevel: 100, dialogue: 500, maxReturnMinutes: 525_600 } as const;
export const MAP_MONSTER_ID_PATTERN = /^[a-z0-9-]{4,40}$/;

export const newMapMonsterId = (): string => `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
